import { makeModuleStateSelector } from "@client/content/selectors";
import { globals } from "@client/globals";
import {
  combinedSettingsSelector,
  combinedStyleSelector,
  createShallowEqualSelector,
  eventsItemsSelector,
  TargetSelector
} from "@client/selectors";
import { Status } from "@client/types";
import { isStaffOrSuperUser } from "@common/appInfo";
import classnames from "classnames";
import { createSelector } from "reselect";
import { defaultState, previewState } from "./reducer";
import { type Checkout } from "./types";

export const apiUrlSelector = createSelector([], () => globals.paths.apiUrl);
export const isBillingPropsSelector = (state: any, props: { isBilling: boolean }) => props.isBilling;

export const localStateSelector = makeModuleStateSelector<Checkout.State>(defaultState, previewState);

export const localStateUserAddressesSelector = createSelector([localStateSelector], (ls) => ls.userAddresses);
export const localStateDefaultBillingAddressIdSelector = createSelector(
  [localStateSelector],
  (ls): string | null => ls.defaultBillingAddressId
);
export const localStateDefaultShippingAddressIdSelector = createSelector(
  [localStateSelector],
  (ls): string | null => ls.defaultShippingAddressId
);
export const makeAddressSelector = () => {
  return createSelector(
    [
      localStateUserAddressesSelector,
      localStateDefaultBillingAddressIdSelector,
      localStateDefaultShippingAddressIdSelector,
      isBillingPropsSelector
    ],
    (userAddresses, defaultBillingAddressId, defaultShippingAddressId, isBilling): Checkout.Api.UserAddress | null => {
      if (isBilling) {
        return userAddresses.find((x) => x.addressId === defaultBillingAddressId) ?? null;
      }
      return (
        userAddresses.find((x) => x.addressId === defaultShippingAddressId) ??
        userAddresses.find((x) => x.addressId === defaultBillingAddressId) ??
        null
      );
    }
  );
};

export const makeCanPlaceOrderSelector = () =>
  createSelector(
    [localStateSelector],
    (localState): boolean =>
      localState.status === Status.Ready &&
      localState.cart.items.every((x) => x.availableQuantity && x.availableQuantity >= x.quantity) &&
      (!!localState.cart.paymentMethods.length || localState.cart.dueAmount === 0) &&
      (!localState.cart.requiresShipping || !!localState.cart.shippingRules.length) &&
      !localState.shouldAdjust &&
      !isStaffOrSuperUser()
  );

const makeLocalStateProductVariantIdSelector = () =>
  createSelector([localStateSelector], (localState): string | null => localState.productVariantId);

type TriggerInfoSettings = {
  funnelPathId?: string;
  currencyCode: string;
  offerProductId?: string | null;
  offerProductVariantId?: string;
  productId?: string | null;
  productVariantId?: string | null;
  includeOffer?: boolean;
};
export const makeOrderInfoTriggerParamsSelector = () => {
  const localStateProductVariantIdSelector = makeLocalStateProductVariantIdSelector();
  const settingsSelector = combinedSettingsSelector<TriggerInfoSettings>(TargetSelector.Entity, [
    { name: "currency", prop: "currencyCode" },
    { name: "funnelPath", prop: "funnelPathId", path: "redirect" },
    { name: "product", prop: "productId" },
    { name: "product", prop: "offerProductId", path: "offer" },
    { name: "productVariant", prop: "offerProductVariantId", path: "offer" }
  ]);

  return createShallowEqualSelector(
    [settingsSelector, localStateProductVariantIdSelector],
    (
      { offerProductId, offerProductVariantId, productVariantId, ...settings },
      localStateProductVariantId
    ): Checkout.Api.GetOrderInfoTriggerParams => ({
      ...settings,
      productVariantId: localStateProductVariantId,
      offerProductVariantId: offerProductId ? offerProductVariantId : null
    })
  );
};

export const eventOptionItemsSelector = createSelector([eventsItemsSelector], (events): Checkout.EventOptionItem[] =>
  events.map((x) => ({ label: x.name, portalId: x.portalId, value: x.id }))
);

export type OfferSettings = {
  description?: string;
  title?: string;
  descriptionClassName: string;
  imageSize: string;
  imageStyle: React.CSSProperties;
  mainContainerStyle: React.CSSProperties;
};

export const makeOfferSettingsSelector = () => {
  const mainStyleSelector = combinedStyleSelector(
    TargetSelector.Entity,
    ["boxShadow", "border", "background", "margin", "padding"],
    "offer"
  );

  const imageStyleSelector = combinedStyleSelector(TargetSelector.Entity, ["boxShadow", "border"], "offer.image");

  const settingsSelector = combinedSettingsSelector(
    TargetSelector.Entity,
    [
      "description",
      { name: "title", prop: "title" },
      { name: "orientation", prop: "imageOrientation", path: "image" },
      { name: "size", prop: "imageSize", path: "image" }
    ],
    "offer"
  );

  return createShallowEqualSelector(
    [settingsSelector, mainStyleSelector, imageStyleSelector],
    (settings, mainStyle, imageStyle): OfferSettings => ({
      description: settings.description,
      descriptionClassName: classnames({
        img_right: settings.imageOrientation === "right"
      }),
      title: settings.title,
      mainContainerStyle: mainStyle,
      imageStyle: imageStyle,
      imageSize: settings.imageSize
    })
  );
};
